import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect } from 'react';
import CarouselModal from '../../Modals/CarouselModal';

import URLPreviewPlugin from './URLPreviewPlugin';
import EmailPreviewPlugin from './EmailPreviewPlugin';
import ImagePreviewPlugin from './ImagePreviewPlugin';
import MessageColorPalette from '../MessageColorPalette';
import FilePreviewPlugin from './FilePreviewPlugin';
import AIInsightsPlugin from './AIInsightsPlugin';

import styled from 'styled-components';
import customAnalytics from '../../../util/custom-analytics';
import { isFileTypeImage, CLOUDINARY_SIZE_LIMIT } from '../../../constants/fileUtils';
import { IoMdHeart, IoMdRepeat, IoMdChatbubbles } from 'react-icons/io';
import { PALE_WHITE_1, PALE_WHITE_6, RED } from '../../../appearance/Colors';

function PluginManager({
    message,
    files,
    clearDropzoneFiles,
    enableAIInsightsPlugin = false,
    showAttachAction = true,
    onOpenVideoWorkspace,
    hideUrlTranscriptAction = false,
    onOpenArticleVoiceModal,
    isGeneratingArticleAudio = false,
    readOnly = false,
    canGenerate = true
}) {
    const {t: lnT} = useTranslation();

    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [dropzoneImages, setDropzoneImages] = useState(undefined);
    const [dropzoneFiles, setDropzoneFiles] = useState(undefined);
    const attachInputRef = React.useRef(null);
    const hasImageAttachment = Boolean(message?.image);
    const hasFileAttachment = Boolean(message?.file);
    // const [urls, setUrls] = useState([]);

    useEffect(() => {
        if (!readOnly && files) {
            decideOnFiles();
        }
        return () => {
            // clearDropzoneFiles();
        };
    }, [files]);

    useEffect(() => {
        if (hasImageAttachment && dropzoneImages?.length) {
            setDropzoneImages(undefined);
        }
        if (hasFileAttachment && dropzoneFiles?.length) {
            setDropzoneFiles(undefined);
        }
    }, [hasImageAttachment, hasFileAttachment, dropzoneImages?.length, dropzoneFiles?.length]);

    const decideOnFiles = incomingFiles => {
        if (readOnly) return;
        let imageFiles = [];
        let ordinaryFiles = [];

        const filesToProcess = incomingFiles || files;

        if (filesToProcess) {
            filesToProcess.forEach(file => {
                if (!hasImageAttachment && !hasFileAttachment) {
                    if (isFileTypeImage(file.type)) {
                        if (file.size > CLOUDINARY_SIZE_LIMIT) {
                            // Large images go to FilePreviewPlugin (S3)
                            ordinaryFiles.push(file);
                        } else {
                            // Small images go to ImagePreviewPlugin (Cloudinary)
                            imageFiles.push(file);
                        }
                    } else {
                        // Non-images go to FilePreviewPlugin
                        ordinaryFiles.push(file);
                    }
                    return;
                }

                if (hasImageAttachment && !hasFileAttachment) {
                    // Image slot already filled; Attach should only add the file slot.
                    ordinaryFiles.push(file);
                    return;
                }

                if (!hasImageAttachment && hasFileAttachment) {
                    // File slot already filled; Attach should only add the image slot.
                    if (isFileTypeImage(file.type) && file.size <= CLOUDINARY_SIZE_LIMIT) {
                        imageFiles.push(file);
                    }
                }
            });
        }

        const nextDropzoneImages = imageFiles.length ? imageFiles : undefined;
        const nextDropzoneFiles = ordinaryFiles.length ? ordinaryFiles : undefined;

        setDropzoneImages(nextDropzoneImages);
        setDropzoneFiles(nextDropzoneFiles);

        if (nextDropzoneImages) {
            customAnalytics.ui_action({
                "action": "Dropzone active",
                "screen": "MessageScreen",
                "type": "Image"
            });
        }
        if (nextDropzoneFiles) {
            customAnalytics.ui_action({
                "action": "Dropzone active",
                "screen": "MessageScreen",
                "type": "File"
            });
        }
        if (!incomingFiles) {
            clearDropzoneFiles();
        }
    };

    const onAttachClick = () => {
        if (attachInputRef.current) {
            attachInputRef.current.click();
        }
    };

    const onAttachFilesSelected = event => {
        const selectedFiles = Array.from(event.target.files || []);
        if (!selectedFiles.length) {
            return;
        }
        decideOnFiles(selectedFiles);
        event.target.value = '';
    };

    const canAttachMore = !hasImageAttachment || !hasFileAttachment;
    const hasPendingAttachSelections = Boolean(dropzoneImages?.length || dropzoneFiles?.length);
    const shouldShowSingleAttach = !readOnly && showAttachAction && canAttachMore && !hasPendingAttachSelections;
    const attachAccept = !hasImageAttachment && hasFileAttachment ? 'image/*, .heic' : undefined;

    const renderUrlMeta = () => {
        if (message.urlMeta) {
            return message.urlMeta.map((metaData, index) => {
                return (
                    <URLPreviewPlugin
                        readOnly={readOnly}
                        canGenerate={canGenerate}
                        key={index}
                        index={index}
                        message={message}
                        metaData={metaData}
                        style={{ marginBottom: MARGIN }}
                        onOpenVideoWorkspace={onOpenVideoWorkspace}
                        hideTranscriptAction={hideUrlTranscriptAction}
                        onOpenArticleVoiceModal={onOpenArticleVoiceModal}
                        isGeneratingArticleAudio={isGeneratingArticleAudio}
                    />
                );
            });
        }
    };

    const renderImagePreview = () => {
        return (
            <ImagePreviewPlugin
                onOpenVideoWorkspace={onOpenVideoWorkspace}
                readOnly={readOnly}
                message={message}
                style={{ marginBottom: MARGIN }}
                dropzoneFiles={dropzoneImages}
                hideAddButton={readOnly || canAttachMore}
                onClearPendingSelection={() => setDropzoneImages(undefined)}
                // image={message.image}
            />
        );
        // setUrls([message.image.url])
    };

    const renderFilePreview = () => {
        return (
            <FilePreviewPlugin
                onOpenVideoWorkspace={onOpenVideoWorkspace}
                readOnly={readOnly}
                canGenerate={canGenerate}
                message={message}
                style={{ marginBottom: MARGIN }}
                dropzoneFiles={dropzoneFiles}
                hideAddButton={readOnly || canAttachMore}
                onClearPendingSelection={() => setDropzoneFiles(undefined)}
            />
        );
    };

    const renderSocialMetrics = () => {
        if (!message?.socialMetrics) {
            return null;
        }

        const { like_count, retweet_count, reply_count } = message.socialMetrics;

        const MetricItem = ({ icon: Icon, count, color }) => (
            <div style={{ display: 'flex', alignItems: 'center', marginRight: 16, color: PALE_WHITE_6, fontSize: 13 }}>
                <Icon size={14} style={{ marginRight: 4, color: color || PALE_WHITE_6 }} />
                <span>{count || 0}</span>
            </div>
        );

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '8px 12px',
                    border: `1px solid ${PALE_WHITE_1}`,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    borderRadius: 12,
                    marginBottom: MARGIN
                }}
            >
                <MetricItem icon={IoMdHeart} count={like_count} color={RED} />
                <MetricItem icon={IoMdRepeat} count={retweet_count} color="#00ba7c" />
                <MetricItem icon={IoMdChatbubbles} count={reply_count} />
            </div>
        );
    };

    const renderAIInsights = () => {
        if (!enableAIInsightsPlugin) {
            return null;
        }
        return <AIInsightsPlugin message={message} />;
    };

    if (message) {
        return (
            <React.Fragment>
                {shouldShowSingleAttach ? (
                    <AttachWrapper onClick={onAttachClick} style={{ marginBottom: MARGIN }}>
                        <span style={{ color: 'white' }}>{lnT('messages:attach')}</span>
                        <input
                            type="file"
                            id="message-attach"
                            ref={attachInputRef}
                            accept={attachAccept}
                            style={{ display: 'none' }}
                            onChange={onAttachFilesSelected}
                        />
                    </AttachWrapper>
                ) : null}
                <EmailPreviewPlugin message={message} onOpenVideoWorkspace={onOpenVideoWorkspace} />
                {renderImagePreview()}
                {renderFilePreview()}
                {!message?.image && String(message?.file?.mime || message?.file?.type || '').toLowerCase().startsWith('image/') ? <MessageColorPalette message={message} /> : null}
                {renderUrlMeta()}
                {!message?.image && !String(message?.file?.mime || message?.file?.type || '').toLowerCase().startsWith('image/') ? <MessageColorPalette message={message} /> : null}
                {renderSocialMetrics()}
                {renderAIInsights()}
            </React.Fragment>
        );
    }
    return null;
}

const MARGIN = 8;

const AttachWrapper = styled.div`
    border-radius: 8px;
    border: 1px solid ${PALE_WHITE_1};
    background-color: rgba(255, 255, 255, 0.05);
    cursor: pointer;
    padding: 8px 12px;
    display: flex;
    justify-content: center;
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

export default PluginManager;
